import { create } from 'zustand';
import { db } from '@/lib/db/db';
import type { Athlete, AudioMode, OfflineMode } from '@/types';

/* ─── Settings Store ─────────────────────────────────────────── */

interface SettingsState {
    audioMode: AudioMode;
    offlineMode: OfflineMode;
    setAudioMode: (mode: AudioMode) => Promise<void>;
    setOfflineMode: (mode: OfflineMode) => Promise<void>;
    hydrate: () => Promise<void>;
}

export const useSettingsStore = create<SettingsState>((set) => ({
    audioMode: 'on',
    offlineMode: 'auto',

    setAudioMode: async (mode) => {
        await db.appSettings.update(1, { audioMode: mode });
        set({ audioMode: mode });
    },

    setOfflineMode: async (mode) => {
        await db.appSettings.update(1, { offlineMode: mode });
        set({ offlineMode: mode });
    },

    hydrate: async () => {
        const s = await db.appSettings.get(1);
        if (s) set({ audioMode: s.audioMode, offlineMode: s.offlineMode });
    },
}));

/* ─── Athlete Store ──────────────────────────────────────────── */

interface AthleteState {
    athletes: Athlete[];
    activeId: string;
    hydrated: boolean;
    setActiveId: (id: string) => Promise<void>;
    hydrate: (accountId?: number) => Promise<void>;
}

export const useAthleteStore = create<AthleteState>((set) => ({
    athletes: [],
    activeId: 'ath-1',

    hydrated: false,

    setActiveId: async (id) => {
        await db.appSettings.update(1, { activeAthleteId: id });
        set({ activeId: id });
    },

    hydrate: async (accountId?: number) => {
        let all: Athlete[] = [];
        if (accountId) {
            all = await db.athletes.where('accountId').equals(accountId).toArray();

            // Safety: If logged in but no athlete linked (e.g. migration/error), create one
            if (all.length === 0) {
                const account = await db.accounts.get(accountId);
                if (account) {
                    const athleteId = `ath-${accountId}-${Math.floor(Math.random() * 1000)}`;
                    await db.athletes.add({
                        id: athleteId,
                        accountId,
                        name: account.email.split('@')[0] || 'Athlete',
                        level: 'Beginner',
                        experienceYears: 0,
                        avatar: account.role === 'athlete' ? '🤼' : account.role === 'coach' ? '📋' : '🏠',
                        createdAt: new Date().toISOString()
                    });
                    all = await db.athletes.where('accountId').equals(accountId).toArray();
                }
            }
        } else {
            all = await db.athletes.toArray();
        }

        const s = await db.appSettings.get(1);

        // If the activeId doesn't belong to the current set of athletes, pick the first one
        let activeId = s?.activeAthleteId ?? 'ath-1';
        if (all.length > 0 && !all.find(a => a.id === activeId)) {
            activeId = all[0]?.id ?? 'ath-1';
        }

        set({ athletes: all, activeId, hydrated: true });
    },
}));

/* ─── Training Store ─────────────────────────────────────────── */

type TimerPhase = 'idle' | 'work' | 'rest' | 'done';

interface TrainingState {
    phase: TimerPhase;
    currentRound: number;
    totalRounds: number;
    workSeconds: number;
    restSeconds: number;
    elapsed: number;        // ms into current phase
    totalElapsed: number;   // ms of entire session
    presetId: string | null;
    position: 'neutral' | 'top' | 'bottom';
    whistleStart: boolean;
    cueMode: 'basic' | 'none';

    // New Stance & Motion Config
    trainMode: 'standard' | 'stance_motion' | 'whistle_starts';
    stanceCommands: string[]; // e.g. ['sprawl', 'circle', 'shot']
    commandInterval: { min: number; max: number };

    configure: (cfg: Partial<Pick<TrainingState,
        'totalRounds' | 'workSeconds' | 'restSeconds' | 'presetId' | 'position' | 'whistleStart' | 'cueMode' | 'trainMode' | 'stanceCommands' | 'commandInterval'
    >>) => void;
    start: () => void;
    tick: (deltaMs: number) => void;
    pause: () => void;
    resume: () => void;
    end: () => void;
    reset: () => void;
}

export const useTrainingStore = create<TrainingState>((set, get) => ({
    phase: 'idle',
    currentRound: 1,
    totalRounds: 5,
    workSeconds: 30,
    restSeconds: 15,
    elapsed: 0,
    totalElapsed: 0,
    presetId: null,
    position: 'neutral',
    whistleStart: true,
    cueMode: 'basic',

    trainMode: 'standard',
    stanceCommands: ['stance', 'motion'],
    commandInterval: { min: 2000, max: 5000 },

    configure: (cfg) => set(cfg),

    start: () => set({ phase: 'work', currentRound: 1, elapsed: 0, totalElapsed: 0 }),

    tick: (deltaMs) => {
        const s = get();
        if (s.phase !== 'work' && s.phase !== 'rest') return;

        const newElapsed = s.elapsed + deltaMs;
        const newTotal = s.totalElapsed + deltaMs;
        const phaseDuration = (s.phase === 'work' ? s.workSeconds : s.restSeconds) * 1000;

        if (newElapsed >= phaseDuration) {
            // Phase finished
            if (s.phase === 'work') {
                if (s.currentRound >= s.totalRounds) {
                    set({ phase: 'done', elapsed: 0, totalElapsed: newTotal });
                } else {
                    set({ phase: 'rest', elapsed: 0, totalElapsed: newTotal });
                }
            } else {
                // rest → next work round
                set({ phase: 'work', currentRound: s.currentRound + 1, elapsed: 0, totalElapsed: newTotal });
            }
        } else {
            set({ elapsed: newElapsed, totalElapsed: newTotal });
        }
    },

    pause: () => set({ phase: 'idle' }),

    resume: () => {
        const s = get();
        // Resume to whatever phase was — infer from elapsed
        if (s.elapsed > 0 || s.currentRound > 1 || s.totalElapsed > 0) {
            set({ phase: 'work' });
        }
    },

    end: () => set({ phase: 'done' }),

    reset: () => set({
        phase: 'idle', currentRound: 1, elapsed: 0, totalElapsed: 0,
    }),
}));
